// Analytics/services/endpoints/finaces.js
import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + "/analytics";

// ============================================
// MOCK CONFIGURATION
// ============================================
const MOCK_CONFIG = {
    enabled: import.meta.env.VITE_MOCK_FINANCES === 'true',
    endpoints: {
        'total-billing': true,
        'total-discount': true,
        'total-income': true,
        'billing-by-location': true,
        'billing-by-insurance': true,
        'billing-by-department': true,
        'monthly-billing-trends': true,
        'outpatient-billing-trends': true,
        'category-billing-trends': true,
        'patient-category-billing': true,
    },
    delay: 300
};

// ============================================
// MOCK DATA GENERATORS
// ============================================

const getMockTotalBillingResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [45000, 52000, 48000, 55000, 51000, 58000, 60000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 369000
    }
});

const getMockTotalDiscountResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Discounts", data: [4500, 5200, 4800, 5500, 5100, 5800, 6000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 36900
    }
});

const getMockTotalIncomeResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Income", data: [40500, 46800, 43200, 49500, 45900, 52200, 54000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 332100
    }
});

const getMockBillingByLocationResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [150000, 120000, 99000] }
        ],
        categories: ["Main Hospital", "Emergency", "Outpatient"],
        count: 369000
    }
});

const getMockBillingByInsuranceResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [184500, 110700, 73800] }
        ],
        categories: ["Private Insurance", "Public Insurance", "Self-Pay"],
        count: 369000
    }
});

const getMockBillingByDepartmentResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Billing", data: [120000, 80000, 60000, 50000, 59000] }
        ],
        categories: ["Cardiology", "Neurology", "Orthopedics", "Pediatrics", "Other"],
        count: 369000
    }
});

const getMockMonthlyBillingTrendsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Total Billing", data: [45000, 52000, 48000, 55000, 51000, 58000, 60000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 369000
    }
});

const getMockOutpatientBillingTrendsResponse = () => ({
    status: 200,
    chartData: {
        series: [
            { name: "Outpatient Billing", data: [30000, 35000, 32000, 38000, 34000, 40000, 42000] }
        ],
        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        count: 251000
    }
});

// CategoryBillingTable consumes `stats.categoryTotals` (a map keyed by category),
// not a `lines` array — see CategoryBillingTable.tsx. Anything else falls through
// to its "No billing data" empty state, which is why this mock must match the
// categoryTotals shape exactly.
const getMockCategoryBillingTrendsResponse = () => ({
    status: 200,
    stats: {
        categoryTotals: {
            "Consultation": { totalBilled: 120000, totalPaid: 96000, totalPending: 24000, totalDiscount: 6000, totalExternalPaymentPending: 4000, totalExternalPaymentPaid: 8000, count: 500 },
            "Laboratory":   { totalBilled: 80000,  totalPaid: 72000, totalPending: 8000,  totalDiscount: 3000, totalExternalPaymentPending: 1500, totalExternalPaymentPaid: 5000, count: 300 },
            "Radiology":    { totalBilled: 60000,  totalPaid: 51000, totalPending: 9000,  totalDiscount: 2500, totalExternalPaymentPending: 2000, totalExternalPaymentPaid: 3000, count: 200 },
            "Pharmacy":     { totalBilled: 50000,  totalPaid: 47000, totalPending: 3000,  totalDiscount: 1500, totalExternalPaymentPending: 500,  totalExternalPaymentPaid: 4000, count: 400 },
            "Other":        { totalBilled: 59000,  totalPaid: 50000, totalPending: 9000,  totalDiscount: 2000, totalExternalPaymentPending: 1000, totalExternalPaymentPaid: 2500, count: 250 }
        }
    }
});

// PatientCategoryTable consumes `stats.lines` directly. These lines are exactly what
// `getBillingPerPatientByServiceCategory` answers with, one row per BILL -- the contract is
// documented on `PatientCategoryBillingLine` in `../../types`.
//
// SHER-957: `patientInvestigationUuid` used to be null here, paired with an invented `patient`
// name the endpoint never sends. Both are gone. The uuids below are real-looking and absent from
// the local demo DB, so the lookup fails and the table renders the uuid itself -- which is the
// production path for an unreadable patient, where null was a state production never reaches.
const getMockPatientCategoryBillingResponse = () => ({
    status: 200,
    chartData: null,
    stats: {
        lines: [
            {
                billUuid: "bill-uuid-1",
                billId: 1,
                billReference: "BILL-001",
                billTotal: 125000,
                billTotalPaid: 125000,
                totalDiscount: 0,
                department: "Cardiology",
                departmentUuid: "dept-uuid-1",
                patientInvestigationUuid: "9f2a1c7e-4b30-4d18-9a55-6c1d0e8b2f41",
                practitioner: "Dr. Smith",
                categoryTotals: { "Consultation": 100000, "Pharmacy": 25000 },
                totalAmountForPercentage: 125000,
                totalPaidFromPayments: 125000,
                categoryAllocations: { "Consultation": 100000, "Pharmacy": 25000 },
                paymentMethods: { "Cash": { amount: 80000, count: 1 }, "Card": { amount: 45000, count: 1 } },
                paymentDates: ["2024-01-14T09:05:00Z", "2024-01-15T10:30:00Z"],
                earliestPaymentDate: "2024-01-14T09:05:00Z",
                latestPaymentDate: "2024-01-15T10:30:00Z",
                totalPaidInPeriod: 125000
            },
            {
                billUuid: "bill-uuid-2",
                billId: 2,
                billReference: "BILL-002",
                billTotal: 98000,
                billTotalPaid: 98000,
                totalDiscount: 0,
                department: "Laboratory",
                departmentUuid: "dept-uuid-2",
                patientInvestigationUuid: "1c84b6d2-77ae-4f09-b3e1-2d95a0cf7b63",
                practitioner: "Dr. Jones",
                categoryTotals: { "Laboratory": 98000 },
                totalAmountForPercentage: 98000,
                totalPaidFromPayments: 98000,
                categoryAllocations: { "Laboratory": 98000 },
                paymentMethods: { "Insurance": { amount: 98000, count: 1 } },
                paymentDates: ["2024-01-16T14:20:00Z"],
                earliestPaymentDate: "2024-01-16T14:20:00Z",
                latestPaymentDate: "2024-01-16T14:20:00Z",
                totalPaidInPeriod: 98000
            },
            {
                billUuid: "bill-uuid-3",
                billId: 3,
                billReference: "BILL-003",
                billTotal: 75000,
                billTotalPaid: 75000,
                totalDiscount: 0,
                department: "Radiology",
                departmentUuid: "dept-uuid-3",
                patientInvestigationUuid: "b5e309af-1d62-4c88-8f47-70ba3e5d91c2",
                practitioner: "Dr. Williams",
                categoryTotals: { "Radiology": 75000 },
                totalAmountForPercentage: 75000,
                totalPaidFromPayments: 75000,
                categoryAllocations: { "Radiology": 75000 },
                paymentMethods: { "Cheque": { amount: 75000, count: 1, bankName: "Ecobank", chequeNumber: "00231" } },
                paymentDates: ["2024-01-17T09:15:00Z"],
                earliestPaymentDate: "2024-01-17T09:15:00Z",
                latestPaymentDate: "2024-01-17T09:15:00Z",
                totalPaidInPeriod: 75000
            },
            {
                billUuid: "bill-uuid-4",
                billId: 4,
                billReference: "BILL-004",
                billTotal: 43000,
                billTotalPaid: 43000,
                totalDiscount: 0,
                department: "Pharmacy",
                departmentUuid: "dept-uuid-4",
                patientInvestigationUuid: "42d7c0f8-9e51-4a36-bd94-1f60c8a7e3d5",
                practitioner: "Dr. Davis",
                categoryTotals: { "Pharmacy": 43000 },
                totalAmountForPercentage: 43000,
                totalPaidFromPayments: 43000,
                categoryAllocations: { "Pharmacy": 43000 },
                paymentMethods: { "Cash": { amount: 43000, count: 1 } },
                paymentDates: ["2024-01-18T11:45:00Z"],
                earliestPaymentDate: "2024-01-18T11:45:00Z",
                latestPaymentDate: "2024-01-18T11:45:00Z",
                totalPaidInPeriod: 43000
            },
            {
                billUuid: "bill-uuid-5",
                billId: 5,
                billReference: "BILL-005",
                billTotal: 28000,
                billTotalPaid: 28000,
                totalDiscount: 0,
                department: "Neurology",
                departmentUuid: "dept-uuid-5",
                patientInvestigationUuid: "7a1fb493-6c28-4e70-95db-3e82d40f16a9",
                practitioner: "Dr. Miller",
                categoryTotals: { "Consultation": 28000 },
                totalAmountForPercentage: 28000,
                totalPaidFromPayments: 28000,
                categoryAllocations: { "Consultation": 28000 },
                paymentMethods: { "Card": { amount: 28000, count: 1 } },
                paymentDates: ["2024-01-19T16:00:00Z"],
                earliestPaymentDate: "2024-01-19T16:00:00Z",
                latestPaymentDate: "2024-01-19T16:00:00Z",
                totalPaidInPeriod: 28000
            }
        ]
    }
});

// Mock registry
const MOCK_REGISTRY = {
    'total-billing': getMockTotalBillingResponse,
    'total-discount': getMockTotalDiscountResponse,
    'total-income': getMockTotalIncomeResponse,
    'billing-by-location': getMockBillingByLocationResponse,
    'billing-by-insurance': getMockBillingByInsuranceResponse,
    'billing-by-department': getMockBillingByDepartmentResponse,
    'monthly-billing-trends': getMockMonthlyBillingTrendsResponse,
    'outpatient-billing-trends': getMockOutpatientBillingTrendsResponse,
    'category-billing-trends': getMockCategoryBillingTrendsResponse,
    'patient-category-billing': getMockPatientCategoryBillingResponse,
};

const getAuthHeaders = () => ({
    headers: { "Authorization": localStorage.getItem("jwt") }
});

const buildOutpatientsUrl = (endpoint, { uuidInvestigation, uuidDepartment, startDate, endDate }) => {
    const baseUrl = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/finances`;
    const endpointPath = endpoint || '';
    console.log('Building URL:', endpointPath, uuidDepartment, startDate, endDate);
    return `${baseUrl}/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
};

const makeRequest = async (params) => {
    const { endpointPath } = params;
    const url = buildOutpatientsUrl(endpointPath, params);
    console.log('Making request to:', params, endpointPath);
    
    // Return mock data if enabled
    if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
        console.log(`[MOCK] Returning mock data for: ${endpointPath}`);
        await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.delay));
        return MOCK_REGISTRY[endpointPath]();
    }
    
    try {
        const response = await axios.get(url, getAuthHeaders());
        if (response.status === 200) {
            console.log('Finances API Response:', endpointPath, response.data);
            return response.data;
        }
        return response.data;
    } catch (error) {
        console.error('Error fetching finances data:', error);
    }
};

const createEndpoint = (endpointPath) => {
    console.log('Creating finances endpoint:', endpointPath);
    return datalogger((uuidInvestigation, uuidDepartment, startDate, endDate) => 
        makeRequest({ 
            endpointPath,
            uuidInvestigation, 
            uuidDepartment, 
            startDate, 
            endDate 
        })
    );
};

export const financesServices = {
    getTotalBilling: createEndpoint('total-billing'),
    getTotalDiscount: createEndpoint('total-discount'), 
    getTotalIncome: createEndpoint('total-income'),
    getBillingByLocation: createEndpoint('billing-by-location'),
    getBillingByInsurance: createEndpoint('billing-by-insurance'),
    getBillingByDepartment: createEndpoint('billing-by-department'),
    getMonthlyBillingTrends: createEndpoint('monthly-billing-trends'),
    getOutpatientBillingTrends: createEndpoint('outpatient-billing-trends'),
    getCategoryBillingTrends: createEndpoint('category-billing-trends'),
    getPatientCategoryBilling: createEndpoint('patient-category-billing'),
};

export const financesEndpoints = {
    TOTAL_BILLING: '/total-billing',
    TOTAL_DISCOUNT: '/total-discount',
    TOTAL_INCOME: '/total-income',
    BILLING_BY_LOCATION: '/billing-by-location',
    BILLING_BY_INSURANCE: '/billing-by-insurance',
    BILLING_BY_DEPARTMENT: '/billing-by-department',
    BILLING_HISTORICAL: '/billing-historical',
    OUTPATIENTS_BILLING: '/outpatients-billing',
    INPATIENTS_BILLING: '/inpatients-billing'
};
